import { useState } from 'react';
import { Box, Typography, Paper, Button, Divider, Alert, Tooltip } from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';
import { saveBlob, blobErrorBody } from '../downloadFile';

/**
 * Documento del contrato (PLAN_ERP.md §15, fase B1, 2026-10-19): el contrato
 * tipo en PDF con los datos de la ficha, la cotización y el contrato. El
 * detalle trae el chequeo (contractDocument): sin plantilla activa o con datos
 * obligatorios vacíos, el botón queda deshabilitado con el motivo. La firma
 * con Izydocs (fase B2) va en ErpContractSignatureCard.
 */
const ErpContractDocumentCard = ({ contract, quotation, contractDocument }) => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();
  const [working, setWorking] = useState(false);
  const k = (key) => `erp.contracts.document.${key}`;

  const check = contractDocument;
  const modeLabel = (mode) => (mode ? t(`erp.documentTemplates.serviceModes.${mode}`) : t(k('unknownMode')));
  const kindLabel = (kind) => (kind ? t(`erp.documentTemplates.contractKinds.${kind}`) : t(k('unknownKind')));
  const missingList = (missing) => (missing || []).map((m) => m.label).join(', ');

  let reason = null;
  if (!check) reason = t(k('checkError'));
  else if (check.reason === 'NO_TEMPLATE') reason = t(k('noTemplate'), { mode: modeLabel(check.serviceMode), kind: kindLabel(check.contractKind) });
  else if (check.reason === 'DATA_MISSING') reason = t(k('missing'), { list: missingList(check.missing) });
  else if (check.reason === 'STALE_SIMULATION') reason = t(k('stale'));

  const handleDownload = async () => {
    try {
      setWorking(true);
      const response = await api.post(
        `/erp/contracts/${contract.uid}/contract-document`,
        { organizationId: organization },
        { headers: { Authorization: `Bearer ${authToken}` }, responseType: 'blob' }
      );
      saveBlob(response.data, `contrato_${quotation?.quotationNumber || contract.workerRut || contract.uid}.pdf`, 'application/pdf');
    } catch (err) {
      console.error(err);
      const data = await blobErrorBody(err);
      if (data?.code === 'NO_TEMPLATE') {
        showAlert(t(k('noTemplate'), { mode: modeLabel(data.serviceMode), kind: kindLabel(data.contractKind) }), 'warning');
      } else if (data?.code === 'CONTRACT_DATA_MISSING') {
        showAlert(t(k('missing'), { list: missingList(data.missing) }), 'warning');
      } else if (data?.code === 'STALE_SIMULATION') {
        showAlert(t(k('stale')), 'warning');
      } else {
        showAlert(t(k('error')), 'error');
      }
    } finally {
      setWorking(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>{t(k('title'))}</Typography>
        <Tooltip title={reason || t(k('downloadHint'))}>
          <span>
            <Button size="small" variant="contained" startIcon={<PdfIcon />}
              disabled={working || !check?.ready} onClick={handleDownload} sx={{ textTransform: 'none' }}>
              {t(k('download'))}
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Divider sx={{ my: 1 }} />
      {check?.template && (
        <Typography variant="body2" sx={{ mb: reason ? 1 : 0 }}>
          {t(k('template'), { name: check.template.name })}
        </Typography>
      )}
      {reason && (
        <Alert severity={check ? 'warning' : 'error'}>{reason}</Alert>
      )}
    </Paper>
  );
};

export default ErpContractDocumentCard;
